import React from 'react';
import { channelColorMap, chartAllColors, dateTypeMap } from "@op/constants";
import { ParamsTimeType } from "@op/types/common";
import { getRationPercentValue, getTimeSpaceArray, sortTimeListFunc } from "@op/utils";
import SvgIcon from '@home/components/SvgIcon';
import { IRealtimeSegment } from '../type';
import moment from 'moment';
import { findLastIndex } from "lodash-es";
import { reviewsFormat } from '@op/utils/value';
import { TABLE_LABEL_NAME } from '../constant';

export function dealWithListData(data: any, metricsKey: any) {
  const { list, oldList, colorList } = dealWithTotal(data, metricsKey);
  const keys = metricsKey.map((val) => val.value);
  const sortedList = sortDataList(list, 'name');

  const valueMap = (sortedList || []).map((val, index) => {
    const { name } = val;
    const oldVal = oldList.find((old) => old.name === name);
    return keys.reduce((pre, key) => {
      if (key === 'name') {
        return pre;
      }
      const oldKey = `old_${key}`;
      const ratioKey = `${key}_dod`;

      const value = val?.[key] || 0;
      const oldValue = oldVal?.[key] || 0;
      const ratio = getRationPercentValue(value, oldValue, 6);
      return { ...pre, [key]: value, [oldKey]: oldValue, [ratioKey]: ratio, color: colorList?.[name] || '' };
    }, { name });
  });

  return { topList: valueMap, colorList };
}

function dealWithTotal(data: { list: any; old_list: any }, metricsKey: any) {
  const { list, old_list } = data;

  const total = (list || []).reduce((acc, item) => {
    Object.keys(item).forEach(key => {
      if (key === 'name') return;

      if (!acc[key]) acc[key] = 0;
      acc[key] += item[key];
    });
    return acc;
  }, { name: 'Total' });

  const oldTotal = (old_list || []).reduce((acc, item) => {
    Object.keys(item).forEach(key => {
      if (key === 'name') return;

      if (!acc[key]) acc[key] = 0;
      acc[key] += item[key];
    });
    return acc;
  }, { name: 'Total' });

  metricsKey.forEach((item) => {
    const { value, isAvg = false } = item;
    if (isAvg) {
      total[value] = total[value] / (list?.length || 0);
      oldTotal[value] = oldTotal[value] / (list?.length || 0);
      total.isAvg = true;
      oldTotal.isAvg = true;
    }
  });

  return {
    list: [
      total,
      ...(list || []),
    ],
    oldList: [
      oldTotal,
      ...(old_list || [])
    ],
    colorList: getColorList(list)
  };
};

export function dealWithTimelineData({
  timeline_of_start_time,
  timeline_of_end_time,
  timeline,
  filter,
  metricsKey,
  colorList
}: {
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  timeline: Array<any>;
  colorList?: Array<any>;
  filter: any;
  metricsKey: string[];
}) {
  const { date_type } = filter;
  const dateType = dateTypeMap[date_type as ParamsTimeType];

  let format = 'YYYY-MM-DD';
  if (dateType === 'hour') {
    format = 'YYYY-MM-DD HH:mm:ss';
  } else if (dateType === 'month') {
    format = 'YYYY-MM';
  }
  const timeList = getTimeSpaceArray(
    timeline_of_start_time,
    timeline_of_end_time,
    dateType,
    format,
  );
  const legendData = [...new Set((timeline || []).map(val => val.name))];
  const timelineMap = new Map();
  timeline?.forEach(item => {
    timelineMap.set(`${item.name}-${item.time}`, item);
  });
  const seriesData = metricsKey.reduce((pre, metricKey) => {
    const metricArr = legendData.map((name, index) => {
      const data = timeList.map((time) => {
        const item = timelineMap.get(`${name}-${time}`);
        return item?.[metricKey] ?? 0;
      });

      return {
        name,
        label: name,
        data,
        color: colorList?.[name] || chartAllColors[index],
      };
    });

    return {
      ...pre,
      [metricKey]: metricArr
    };
  }, {});

  return {
    xAxisData: timeList,
    seriesData,
    legendData
  };
}

export function sortDataList(list: Array<any>, sort: string, order: 'asc' | 'desc' = 'desc') {
  return (list || []).sort((a, b) => {
    const aName = a.name?.toLocaleLowerCase?.();
    const bName = b.name?.toLocaleLowerCase?.();

    if (aName === 'others') return 1;
    if (bName === 'others') return -1;

    if (order === 'asc') {
      return a[sort] - b[sort];
    }
    return b[sort] - a[sort];
  });
}

export function getColorList(list: Array<any>) {
  return list.reduce((acc, val, index) => {
    const { name } = val;
    acc[name] = channelColorMap[name] || chartAllColors[index];
    return acc;
  }, {});
}

export function getSegmentWithIcon(options: IRealtimeSegment[], formatMessage: any) {

  return options.map((val) => {
    const { text, value, icon } = val;
    return {
      text: (
        <div className="dbo-inlineflex">
          {icon && <SvgIcon type={icon}></SvgIcon>}
          <span>{formatMessage({ id: text })}</span>
        </div>
      ),
      value,
    };
  });
}

export function dealWithListObjData(data: any, metricsKey: any) {
  const { list, old_list } = data;
  const keys = metricsKey.map((val) => val.value);

  const valueMap = keys.reduce((pre, key) => {

    const oldKey = `old_${key}`;
    const ratioKey = `${key}_dod`;

    const value = list?.[key] || 0;
    const oldValue = old_list?.[key] || 0;
    const ratio = getRationPercentValue(value, oldValue, 6);
    return { ...pre, [key]: value, [oldKey]: oldValue, [ratioKey]: ratio };
  }, {});

  return valueMap;
}

export function getTimeArray(filter: { start_time: string; end_time: string; }, intervalRange: moment.DurationInputArg1 = 5, intervalKey: moment.DurationInputArg2 = 'minutes', format = 'YYYY-MM-DD HH:mm:ss') {
  const { start_time, end_time } = filter;
  const start = moment(start_time, 'YYYY-MM-DD HH:mm:ss');
  const end = moment(end_time, 'YYYY-MM-DD HH:mm:ss');
  const timeList = [];
  while (start <= end) {
    timeList.push(start.format(format));
    start.add(intervalRange, intervalKey);
  }
  return timeList;
}

const checkIsMax = (dateTime: string, maxTime: string) => {
  return moment(dateTime, 'YYYY-MM-DD HH:mm:ss').isAfter(moment(maxTime, 'YYYY-MM-DD HH:mm:ss'));
};

export function dealWithOldTimeline(data: any, timeList: string[], metricsKey: any[]) {
  const { timeline = [], old_timeline, start_time, end_time } = data || {};
  const maxTime = end_time;
  // 不取timeline最后时间，直接用end_time
  // const maxTime = timeline.reduce((latestTime, item) => {
  //   if (!latestTime) return item.time;
  //   const currentMoment = moment(item.time).valueOf();
  //   const latestMoment = moment(latestTime).valueOf();
  //   return currentMoment > latestMoment ? item.time : latestTime;
  // }, null);

  if (!old_timeline) {
    const isAccumulated = metricsKey.some((val) => val.accumulate);
    return isAccumulated ? dealWithAccumulative(timeline, timeList, maxTime) : timeline;
  }

  const date = start_time.split(' ')[0];
  const accumulators: Record<string, number> = {};
  const oldAccumulators: Record<string, number> = {};

  // Initialize accumulators
  metricsKey.forEach((metric) => {
    const { value } = metric;
    accumulators[value] = 0;
    if (value.startsWith('old_')) {
      oldAccumulators[value] = 0;
    }
  });

  const newTimeline = timeList.map((dateTime) => {
    const time = dateTime.split(' ')[1];
    const fullTime = `${date} ${time}`;

    const item = timeline.find((val) => val.time.split(' ')[1] === time) || {};
    const oldItem = old_timeline.find((val) => val.time.split(' ')[1] === time) || {};

    const result: Record<string, any> = {
      time: fullTime,
    };

    metricsKey.forEach((each) => {
      const { value: metric, accumulate = false } = each;
      if (metric.startsWith('old_')) {
        const key = metric.replace('old_', '');
        const value = Number(oldItem?.[key] || 0);
        oldAccumulators[metric] += value;
        result[metric] = accumulate ? oldAccumulators[metric] : value;
      } else {
        const value = Number(item?.[metric] || 0);
        accumulators[metric] += value;
        if (accumulate) {
          result[metric] = checkIsMax(fullTime, maxTime) ? 0 : accumulators[metric];
        } else {
          result[metric] = value;
        }

      }
    });

    return result;
  });

  return newTimeline;
}

export function dealWithAccumulative(timeline: any[], timeList: string[], maxTime: string) {
  const acc: Record<string, number> = {};
  const timelineMap = new Map(timeline.map((item) => [item.time, item]));
  const allKeys = new Set<string>();
  timeline.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'time') {
        allKeys.add(key);
      }
    });
  });
  return timeList.map((time) => {
    const item = timelineMap.get(time) || {};
    const newItem: Record<string, any> = { time };
    const isAfterMax = moment(time).isAfter(moment(maxTime));
    allKeys.forEach((key) => {
      const value = item[key];

      if (typeof value === 'number' || typeof acc[key] === 'number') {
        if (isAfterMax) {
          newItem[key] = 0;
        } else {
          const numericValue = typeof value === 'number' ? value : 0;
          acc[key] = (acc[key] || 0) + numericValue;
          newItem[key] = acc[key];
        }
      } else {
        newItem[key] = value ?? null;
      }
    });

    return newItem;
  });
}

export const addRadarSymbol = (options, formatValue = reviewsFormat) => {
  const { series = [], xAxis } = options;
  const mainSeries = series[0] || {};
  const data = mainSeries.data || [];

  const lastIndex = findLastIndex(data, val => val);
  if (lastIndex === -1) return options;

  const xVal = xAxis?.data?.[lastIndex] || lastIndex;
  const yVal = data[lastIndex];

  const radarSymbolSeries = {
    name: mainSeries.name,
    type: 'effectScatter',
    coordinateSystem: 'cartesian2d',
    data: [[xVal, yVal]],
    color: '#5086F3',
    symbolSize: 3,
    showEffectOn: 'render',
    rippleEffect: {
      color: '#376AE7',
      number: 3,
      scale: 12,
      // brushType: 'stroke'
    },
    label: {
      show: true,
      formatter: ({ value }: { value: [string | number, number] }) => {
        return formatValue(value[1]);
      },
      fontWeight: 'bold',
      position: 'top',
    },
    tooltip: {
      show: false,
    },
    hoverAnimation: true,
    itemStyle: {
      normal: {
        color: 'rgba(80,134,243,0.2)',
        shadowBlur: 10,
        shadowColor: '#333'
      }
    },
    zlevel: 19
  };

  return {
    ...options,
    series: [...series, radarSymbolSeries],
  };
};

export function getSpecialLabelName(value: string) {
  return TABLE_LABEL_NAME?.[value] || value;
}

export const getAccumulatedChartTimeParams = (
  timeString: string,
  isAccumulated = true,
) => {
  if (!isAccumulated) {
    return {
      start_time: moment(timeString)
        .startOf('hour')
        .format('YYYY-MM-DD HH:mm:ss'),
      end_time: moment(timeString).endOf('hour').format('YYYY-MM-DD HH:mm:ss'),
    };
  }

  return {
    start_time: moment(timeString).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    end_time: moment(timeString).format('YYYY-MM-DD HH:mm:ss'),
  };
};


export function fillNullWithZero(data: (number | null)[]): (number | null)[] {
  const result = [...data];

  // Find last valid number index
  const lastValid = result.length - 1 - [...result].reverse().findIndex(v => v !== null);

  if (lastValid === -1) {
    // all null → return unchanged
    return result;
  }

  for (let i = 0; i < result.length; i++) {
    if (result[i] === null) {
      if (i <= lastValid) {
        result[i] = 0; // before or between → 0
      } else {
        // after last valid → keep null
        break;
      }
    }
  }

  return result;
}